import ServicesExperience from './services/ServicesExperience';

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// New redesigned pages
import DugoutSiteHome, { DugoutContact } from "./pages/DugoutSiteHome";

// Legacy pages (kept for backwards compatibility)
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import Solutions from './pages/Solutions';
import ProductSolution from './pages/solutions/ProductSolution';
import MarketingSolution from './pages/solutions/MarketingSolution';
import CommunitySolution from './pages/solutions/CommunitySolution';
import BusinessSolution from './pages/solutions/BusinessSolution';

// Service Category Pages (Moved from Solutions)

// Service pages

// Portfolio pages
import PortfolioIndex from './pages/portfolio/PortfolioIndex';
import ClientPortfolio from './pages/portfolio/ClientPortfolio';

// Resume/CV pages
import JayPatelResume from './pages/cv/JayPatelResume';

import Cookies from "js-cookie";
import { useState, useEffect, lazy, Suspense } from 'react';
const Learning = lazy(() => import('./pages/Learning'));

const queryClient = new QueryClient();

// Detect basename based on hostname and path
// - godugout.com: no basename (serves at root)
// - localhost with /dugout: use /dugout (vite dev server)
// - eastside.is/dugout: use /dugout
const getBasename = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Production: godugout.com serves at root
  if (hostname === 'godugout.com' || hostname === 'www.godugout.com') {
    return '/';
  }

  // Dev server or eastside.is: check if path starts with /dugout
  if (pathname.startsWith('/dugout')) {
    return '/dugout';
  }

  return '/';
};

// ScrollToTop component to ensure scroll behavior on route changes
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

const App = () => {
  const [hasVisited, setHasVisited] = useState<boolean | null>(null);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    // Check if user has visited before
    const visited = Cookies.get('visited') === 'true';
    setHasVisited(visited);

    // Set cookie to remember user has visited
    if (!visited) {
      Cookies.set('visited', 'true', { expires: 30 }); // expires in 30 days
    }

    // Add fade-in effect when transitioning from landing page
    setFadeIn(true);
  }, []);

  // Show minimal loading state while checking visit status
  if (hasVisited === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter basename={getBasename()}>
            <ScrollToTop />
            <div className={`${fadeIn ? 'animate-fade-in' : ''} transition-opacity duration-500`}>
              <Routes>
                {/* New redesigned home - now the default */}
                <Route path="/" element={<DugoutSiteHome />} />
                <Route path="/contact" element={<DugoutContact />} />

                <Route path="/learn/*" element={<Suspense fallback={<div className="p-12">Opening Dugout Academy…</div>}><Learning /></Suspense>} />

                {/* Legacy routes for backwards compatibility */}
                <Route path="/landing" element={<Navigate to="/" replace />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/legacy" element={<Navigate to="/" replace />} />
                <Route path="/login" element={<Login />} />

                {/* Main Services Index */}
                <Route path="/services" element={<ServicesExperience />} />

                {/* Service Category Pages - Moved from Solutions */}
                <Route path="/services/product" element={<ServicesExperience />} />
                <Route path="/services/marketing" element={<ServicesExperience />} />
                <Route path="/services/community" element={<ServicesExperience />} />
                <Route path="/services/business" element={<ServicesExperience />} />
                <Route path="/services/brand-strategy" element={<ServicesExperience />} />

                {/* Legacy Solutions Routes - Redirect to new Service Pages */}
                <Route path="/solutions" element={<Navigate to="/#services" />} />
                <Route path="/solutions/product" element={<Navigate to="/services/product" />} />
                <Route path="/solutions/marketing" element={<Navigate to="/services/marketing" />} />
                <Route path="/solutions/community" element={<Navigate to="/services/community" />} />
                <Route path="/solutions/business" element={<Navigate to="/services/business" />} />

                {/* Service pages */}
                <Route path="/services/product-led-growth" element={<ServicesExperience />} />
                <Route path="/services/customer-advocacy" element={<ServicesExperience />} />
                <Route path="/services/content-marketing" element={<ServicesExperience />} />
                <Route path="/services/corporate-messaging" element={<ServicesExperience />} />
                <Route path="/services/community-management" element={<ServicesExperience />} />
                <Route path="/services/analyst-relations" element={<ServicesExperience />} />
                <Route path="/services/sales-enablement" element={<ServicesExperience />} />

                {/* Portfolio pages */}
                <Route path="/portfolio" element={<PortfolioIndex />} />
                <Route path="/portfolio/clients" element={<ClientPortfolio />} />

                {/* Resume/CV */}
                <Route path="/cv/jaypatel" element={<JayPatelResume />} />

                {/* Redirects from old paths */}
                <Route path="/solutions/categories/product" element={<Navigate to="/solutions/product" />} />
                <Route path="/solutions/categories/marketing" element={<Navigate to="/solutions/marketing" />} />
                <Route path="/solutions/categories/community" element={<Navigate to="/solutions/community" />} />
                <Route path="/solutions/categories/business" element={<Navigate to="/solutions/business" />} />

                {/* Redirects from old service paths */}
                <Route path="/solutions/product-strategy" element={<Navigate to="/solutions/product" />} />
                <Route path="/solutions/product-led-growth" element={<Navigate to="/services/product-led-growth" />} />
                <Route path="/solutions/content-marketing" element={<Navigate to="/services/content-marketing" />} />
                <Route path="/solutions/corporate-messaging" element={<Navigate to="/services/corporate-messaging" />} />
                <Route path="/solutions/customer-advocacy" element={<Navigate to="/services/customer-advocacy" />} />
                <Route path="/solutions/community-management" element={<Navigate to="/services/community-management" />} />
                <Route path="/solutions/analyst-relations" element={<Navigate to="/services/analyst-relations" />} />
                <Route path="/solutions/sales-enablement" element={<Navigate to="/services/sales-enablement" />} />

                {/* Legacy redirects */}
                <Route path="/product-strategy" element={<Navigate to="/solutions/product" />} />

                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
